import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const GA_ID = "G-3QTNX54GLJ";
const AI_REFERRAL_SESSION_KEY = "djreq:ai-referral-sent:v1";
const FIRST_TOUCH_KEY = "djreq:first-touch:v1";
const EVENT_CREATED_KEY = "djreq:event-created-sent:v1";
const INTERNAL_TRAFFIC_KEY = "djreq:internal-traffic";
const INTERNAL_QUERY_FLAG = "djreq_internal";
const CTA_DEDUPE_WINDOW_MS = 1500;

const PAGE_KEY_RULES = [
  [/^\/$/, "home"],
  [/^\/pricing$/, "pricing_public"],
  [/^\/dj-request-app$/, "dj_request_app"],
  [/^\/best-dj-request-app$/, "best_dj_request_app"],
  [/^\/dj-request-system-explained$/, "dj_request_system_explained"],
  [/^\/dj-song-request$/, "dj_song_request"],
  [/^\/dj-request-app-for-line-dancing$/, "dj_request_app_for_line_dancing"],
  [/^\/line-dancing-dj-app$/, "line_dancing_dj_app"],
  [/^\/why-paid-song-requests-work$/, "why_paid_song_requests_work"],
  [/^\/karaoke-queue-management$/, "karaoke_queue_management"],
  [/^\/dj-request-vs-nosongrequests$/, "dj_request_vs_nosongrequests"],
  [/^\/song-request-app-for-djs$/, "song_request_app_for_djs"],
  [/^\/faq$/, "faq"],
  [/^\/signin$/, "signin"],
  [/^\/join\/[^/]+$/, "join_session"],
  [/^\/dj$/, "dj_dashboard"],
  [/^\/wall\/[^/]+(?:\/.*)?$/, "photo_wall"],
  [/^\/album\/[^/]+$/, "album_page"],
  [/^\/return$/, "payment_return"],
  [/^\/feedback$/, "feedback"],
  [/^\/qr-poster$/, "qr_poster"],
];

// Query parameters that are safe to keep in analytics. Everything else (event
// codes, checkout session ids, sign-in redirects, plan intents, ...) is dropped.
const ALLOWED_QUERY_PARAMS = new Set([
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
]);

const PREVIEW_HOST_PATTERNS = [/\.web\.app$/i, /\.firebaseapp\.com$/i, /--/];

function safeStorage(kind) {
  try {
    const store = kind === "local" ? window.localStorage : window.sessionStorage;
    // Touch the store so Safari private mode throws here instead of later.
    store.getItem("__djreq_probe__");
    return store;
  } catch {
    return null;
  }
}

/** Marks the browser as internal/staff traffic when ?djreq_internal=1|0 is present. */
function syncInternalTrafficFlag() {
  if (typeof window === "undefined") return;
  try {
    const flag = new URLSearchParams(window.location.search).get(INTERNAL_QUERY_FLAG);
    if (flag === null) return;
    const store = safeStorage("local");
    if (!store) return;
    if (flag === "0") store.removeItem(INTERNAL_TRAFFIC_KEY);
    else store.setItem(INTERNAL_TRAFFIC_KEY, "1");
  } catch {}
}

export function isInternalTraffic() {
  if (typeof window === "undefined") return false;
  try {
    const host = window.location.hostname;
    if (PREVIEW_HOST_PATTERNS.some((pattern) => pattern.test(host))) return true;
    const store = safeStorage("local");
    return store?.getItem(INTERNAL_TRAFFIC_KEY) === "1";
  } catch {
    return false;
  }
}

// Central guard: never send GA hits in dev/local/preview/internal environments
function canTrack() {
  // Vite-style dev flag (tree-shaken in prod builds)
  if (typeof import.meta !== "undefined" && import.meta.env && import.meta.env.DEV) return false;

  if (typeof window === "undefined") return false;

  const host = window.location.hostname;
  if (host === "localhost" || host === "127.0.0.1" || host === "[::1]") return false;
  if (isInternalTraffic()) return false;

  return !!(window.gtag && GA_ID);
}

function normalizePageKey(pathname = "/") {
  const cleanPath = String(pathname || "/").replace(/\/+$/, "") || "/";
  for (const [pattern, key] of PAGE_KEY_RULES) {
    if (pattern.test(cleanPath)) return key;
  }
  return cleanPath
    .replace(/^\//, "")
    .replace(/[^a-zA-Z0-9/_-]+/g, "")
    .replace(/\//g, "_") || "unknown";
}

/**
 * Remove identifiers from a path before it reaches analytics. Event codes,
 * album ids, and wall codes become route placeholders.
 */
export function redactPath(pathname = "/") {
  const clean = String(pathname || "/").replace(/\/+$/, "") || "/";
  return clean
    .replace(/^\/join\/[^/]+/, "/join/:code")
    .replace(/^\/wall\/[^/]+(\/.*)?$/, "/wall/:code")
    .replace(/^\/album\/[^/]+/, "/album/:id");
}

export function redactSearch(search = "") {
  try {
    const params = new URLSearchParams(search);
    const kept = new URLSearchParams();
    for (const [key, value] of params.entries()) {
      if (ALLOWED_QUERY_PARAMS.has(key)) kept.set(key, value);
    }
    const out = kept.toString();
    return out ? `?${out}` : "";
  } catch {
    return "";
  }
}

function redactedLocation(pathname, search) {
  const path = redactPath(pathname) + redactSearch(search);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

function classifyAiSource() {
  if (typeof window === "undefined") return "";

  const sourceParam = new URLSearchParams(window.location.search).get("utm_source");
  const rawReferrer = document.referrer || "";
  let refHost = "";

  try {
    refHost = rawReferrer ? new URL(rawReferrer).hostname.toLowerCase() : "";
  } catch {
    refHost = "";
  }

  const source = String(sourceParam || "").toLowerCase();

  if (source.includes("chatgpt")) return "chatgpt";
  if (source.includes("gemini")) return "gemini";
  if (source.includes("copilot")) return "copilot";
  if (source.includes("grok")) return "grok";
  if (source.includes("claude")) return "claude";
  if (source.includes("perplexity")) return "perplexity";

  if (refHost.includes("chatgpt.com") || refHost.includes("chat.openai.com")) return "chatgpt";
  if (refHost.includes("gemini.google.com") || refHost.includes("bard.google.com")) return "gemini";
  if (refHost.includes("copilot.microsoft.com")) return "copilot";
  if (refHost.includes("grok.com")) return "grok";
  if (refHost.includes("claude.ai")) return "claude";
  if (refHost.includes("perplexity.ai")) return "perplexity";

  return "";
}

function classifyAcquisitionSource() {
  if (typeof window === "undefined") return "direct";
  const params = new URLSearchParams(window.location.search);
  const utmSource = String(params.get("utm_source") || "").toLowerCase().replace(/[^a-z0-9_.-]/g, "");
  if (utmSource) return utmSource;
  const aiSource = classifyAiSource();
  if (aiSource) return `ai_${aiSource}`;
  try {
    const refHost = document.referrer ? new URL(document.referrer).hostname.toLowerCase() : "";
    if (!refHost) return "direct";
    if (refHost === window.location.hostname) return "internal";
    if (/google\./.test(refHost)) return "google";
    if (/bing\.com$/.test(refHost)) return "bing";
    if (/duckduckgo\.com$/.test(refHost)) return "duckduckgo";
    if (/yahoo\./.test(refHost)) return "yahoo";
    return refHost.replace(/^www\./, "");
  } catch {
    return "direct";
  }
}

/**
 * First-touch attribution for the session: the canonical landing page (no
 * query string, no identifiers) and a coarse acquisition source.
 */
export function getFirstTouch() {
  if (typeof window === "undefined") return { landing_page: "/", acquisition_source: "direct" };
  const store = safeStorage("session");
  try {
    const raw = store?.getItem(FIRST_TOUCH_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.landing_page === "string") return parsed;
    }
  } catch {}

  const firstTouch = {
    landing_page: redactPath(window.location.pathname),
    acquisition_source: classifyAcquisitionSource(),
  };
  try {
    store?.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  } catch {}
  return firstTouch;
}

function sendAiSessionStart(aiSource, pagePath) {
  if (!canTrack() || !aiSource || typeof window === "undefined") return;

  try {
    const store = safeStorage("session");
    if (store?.getItem(AI_REFERRAL_SESSION_KEY)) return;
    window.gtag("event", "ai_referral_session_start", {
      ai_source: aiSource,
      page_path: pagePath,
      page_location: redactedLocation(window.location.pathname, window.location.search),
    });
    store?.setItem(AI_REFERRAL_SESSION_KEY, "1");
  } catch {
    // do nothing; metrics should never block runtime
  }
}

export function useGtagPageview() {
  const location = useLocation();
  const lastTrackedPathRef = useRef("");

  useEffect(() => {
    syncInternalTrafficFlag();
    if (!canTrack()) return;

    const pagePath = redactPath(location.pathname) + redactSearch(location.search);
    if (lastTrackedPathRef.current === pagePath) return;
    lastTrackedPathRef.current = pagePath;

    const aiSource = classifyAiSource();
    const normalizedPageKey = normalizePageKey(location.pathname);
    const firstTouch = getFirstTouch();

    window.gtag("event", "page_view", {
      page_path: pagePath,
      page_location: redactedLocation(location.pathname, location.search),
      page_title: document.title,
      normalized_page_key: normalizedPageKey,
      ai_source: aiSource || "none",
      landing_page: firstTouch.landing_page,
      acquisition_source: firstTouch.acquisition_source,
    });

    sendAiSessionStart(aiSource, pagePath);
  }, [location.pathname, location.search]);
}

/**
 * Track sign-in (existing user). Call after successful auth.
 * @param {string} method - "google" | "email"
 */
export function trackSignIn(method) {
  if (!canTrack()) return;
  window.gtag("event", "sign_in", { method: method || "email" });
}

/**
 * Track sign-up (new user). Call after account creation.
 * @param {string} method - "google" | "email"
 */
export function trackSignUp(method) {
  if (!canTrack()) return;
  const firstTouch = getFirstTouch();
  window.gtag("event", "sign_up", {
    method: method || "email",
    landing_page: firstTouch.landing_page,
    acquisition_source: firstTouch.acquisition_source,
  });
}

const recentCtaClicks = new Map();

/**
 * Track a marketing call-to-action click. Deduplicated per cta_id/destination
 * within a short window so double clicks and StrictMode replays send one hit.
 * @param {{ ctaId: string, destination?: string, plan?: string }} input
 */
export function trackMarketingCta({ ctaId, destination = "", plan = "" } = {}) {
  if (!canTrack() || !ctaId) return;
  try {
    const key = `${ctaId}::${destination}`;
    const now = Date.now();
    const last = recentCtaClicks.get(key) || 0;
    if (now - last < CTA_DEDUPE_WINDOW_MS) return;
    recentCtaClicks.set(key, now);

    const firstTouch = getFirstTouch();
    const payload = {
      cta_id: String(ctaId).slice(0, 60),
      cta_destination: redactPath(String(destination || "").split(/[?#]/)[0] || ""),
      page_path: redactPath(window.location.pathname),
      landing_page: firstTouch.landing_page,
      acquisition_source: firstTouch.acquisition_source,
    };
    if (plan) payload.plan = String(plan).toLowerCase().slice(0, 20);
    window.gtag("event", "marketing_cta_click", payload);
  } catch {}
}

/**
 * Track the first successful event creation in this browser session. Fire only
 * after the server has persisted the session, never on button click.
 * @param {{ plan?: string }} input
 */
export function trackEventCreated({ plan = "" } = {}) {
  if (!canTrack()) return;
  try {
    const store = safeStorage("session");
    if (store?.getItem(EVENT_CREATED_KEY)) return;
    const firstTouch = getFirstTouch();
    const payload = {
      landing_page: firstTouch.landing_page,
      acquisition_source: firstTouch.acquisition_source,
    };
    if (plan) payload.plan = String(plan).toLowerCase().slice(0, 20);
    window.gtag("event", "event_created", payload);
    store?.setItem(EVENT_CREATED_KEY, "1");
  } catch {}
}
